import { memo, useRef, useMemo, useCallback, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Crud, CrudFormComponent, CrudMode, CrudProps, FormItem, getSelectsOptions, SelectsOptions } from '@ohpen/enso-ui';
import { ProductResponse } from '@pmp/api';
import { flattenObjectValues } from '@ohpen/react-utils';

import { getFormSchema } from '../formConfig';
import { ProductsViewProps, Store } from '../interfaces';
import { getAdditionalFieldsSchema } from '../utils';

export type DataType = ProductResponse;

export const ProductView = memo(({ record, valueLists, additionalFields, onClose }: ProductsViewProps): ReactElement => {
  const ref = useRef<CrudFormComponent>(null);
  const [open, setOpen] = useState(true);
  const { t } = useTranslation();

  const initialValues = useMemo(() => {
    return flattenObjectValues(record ?? {});
  }, [record]);

  const additionalFieldsSchema: FormItem | null = useMemo(() => {
    return getAdditionalFieldsSchema(additionalFields);
  }, [additionalFields]);

  const schemaWithAdditionalFields: FormItem[] = useMemo(() => {
    const selectsOptions: SelectsOptions = (valueLists && getSelectsOptions(valueLists)) || {};

    return additionalFieldsSchema ? [...getFormSchema(selectsOptions), additionalFieldsSchema] : getFormSchema(selectsOptions);
  }, [additionalFieldsSchema, valueLists]);

  const onConfirm: <T extends Store>(formData: T) => Promise<void> = useCallback(values => Promise.resolve(), []);

  const handleOnClose = useCallback(() => {
    setOpen(false);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  const crudProps: CrudProps<DataType> = useMemo(() => {
    return {
      ref,
      open,
      schema: schemaWithAdditionalFields,
      title: t('global.labels.viewX', { x: t('pmp.products.labels.product') }),
      initialValues,
      mode: CrudMode.VIEW,
      groups: true,
      onClose: handleOnClose,
      onConfirm,
    };
  }, [open, t, initialValues, handleOnClose, onConfirm, schemaWithAdditionalFields]);

  return <Crud {...crudProps} />;
});
